import React, { useEffect, useState } from "react";
import { Building2, CheckCircle2, Wallet, Globe, Calendar, ArrowDownToLine, Sparkles } from "lucide-react";
import { toast } from "sonner";
import useRequest from "../../../components/hook/use-req";
import Pagination from "../../../components/pagination";
import ReuseableDrawer from "../../../components/resuable-drawer";
import { formatCurrency } from "../../../utils/function";
import {
  OrganizationsListResponse,
  OrgCampaign,
  OrgDetailResponse,
  OrgSummary,
  OrgTransaction,
  formatOrgDate,
  getOrgInitials,
  getOrgStatusStyle,
} from "./types";
import FundWalletModal from "./fund-wallet-modal";
import ApiKeysTab from "./api-keys-tab";

// A single metric tile — same "frosted card" shape used elsewhere (see
// components/marketing/global-admin/stat-card.tsx), duplicated locally
// rather than shared since this page's cards are simpler (no sublabel/value
// className variance needed) and this feature is deliberately independent
// from the rest of the Global Admin surface's components.
const MetricCard: React.FC<{ icon: React.ReactNode; iconClassName: string; label: string; value: React.ReactNode }> = ({
  icon,
  iconClassName,
  label,
  value,
}) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5 flex items-center gap-3 sm:gap-4 shadow-sm min-w-0">
    <span className={`inline-flex items-center justify-center w-11 h-11 rounded-xl shrink-0 ${iconClassName}`}>{icon}</span>
    <div className="min-w-0">
      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">{label}</p>
      <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 truncate">{value}</p>
    </div>
  </div>
);

const TRANSACTION_TYPE_STYLES: Record<string, string> = {
  credit: "bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-400",
  debit: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400",
};

const CAMPAIGN_STATUS_STYLES: Record<string, string> = {
  COMPLETED: "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
  DRAFT: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  ACTIVE: "bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-400",
  SCHEDULED: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  FAILED: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400",
};

// "View Business" — GET /admin/organizations/{id}. A drawer (not a route)
// to match the CRM Businesses page's own click-a-row-to-inspect convention,
// with its own paginated transactions table alongside the wallet metrics.
const OrgDetailsDrawer: React.FC<{ orgId: string | null; onClose: () => void }> = ({ orgId, onClose }) => {
  const token = localStorage.getItem("authToken");
  const [detail, setDetail] = useState<OrgDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"transactions" | "campaigns" | "api-keys">("transactions");
  // Kept as two separate page counters — paging through one list must never
  // move the other, per the ask.
  const [txPage, setTxPage] = useState(1);
  const [campaignPage, setCampaignPage] = useState(1);
  const [isFundModalOpen, setIsFundModalOpen] = useState(false);
  const txPageSize = 20;
  const campaignPageSize = 20;

  const { makeRequest: getOrgDetail } = useRequest("/admin/organizations", "GET", {
    Authorization: `Bearer ${token}`,
  });

  // PATCH /admin/organizations/{id}/cxo-ai-enabled — the one switch for all
  // CXO dashboard AI (panel insights + "ask your data"). Off by default.
  const [aiEnabled, setAiEnabled] = useState<boolean | undefined>(undefined);
  const [aiSaving, setAiSaving] = useState(false);
  // Only a global admin may change it; anyone else sees it locked.
  const canToggleAi = localStorage.getItem("role") === "global_admin";
  const { makeRequest: patchCxoAi } = useRequest("/admin/organizations", "PATCH", {
    Authorization: `Bearer ${token}`,
  });
  useEffect(() => {
    setAiEnabled(detail?.org.cxo_ai_enabled);
  }, [detail?.org.id, detail?.org.cxo_ai_enabled]);
  const toggleCxoAi = async () => {
    if (!orgId || aiSaving || !canToggleAi) return;
    const next = !aiEnabled;
    setAiSaving(true);
    const [response, status] = await patchCxoAi({ cxo_ai_enabled: next }, undefined, `${orgId}/cxo-ai-enabled`);
    setAiSaving(false);
    if (status >= 200 && status < 300 && response?.status !== false) {
      setAiEnabled(typeof response?.cxo_ai_enabled === "boolean" ? response.cxo_ai_enabled : next);
      toast.success(`CXO AI insights turned ${next ? "on" : "off"}`);
    } else {
      toast.error(typeof response?.detail === "string" ? response.detail : "Couldn't update the AI setting.");
    }
  };

  // Reset both to page 1 whenever a different org is opened.
  useEffect(() => {
    setTxPage(1);
    setCampaignPage(1);
    setActiveTab("transactions");
    setDetail(null);
  }, [orgId]);

  const fetchDetail = async () => {
    if (!orgId) return;
    setLoading(true);
    // Both sections' page/limit go in the same request — the endpoint
    // returns transactions and campaigns together — using distinct param
    // names so paging one doesn't touch the other.
    const [response] = (await getOrgDetail(
      undefined,
      { page: txPage, limit: txPageSize, campaign_page: campaignPage, campaign_limit: campaignPageSize },
      orgId
    )) as [OrgDetailResponse, number];
    if (response?.status) setDetail(response);
    setLoading(false);
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, txPage, campaignPage]);

  const currency = detail?.wallet.last_transaction?.currency || "NGN";
  // The API returns `pages`, not `total_pages` — fall back to deriving it
  // from total/limit if neither is present, so pagination still works even
  // if that response shape changes again.
  const txTotalPages = detail
    ? detail.transactions.pages ?? detail.transactions.total_pages ?? Math.max(1, Math.ceil(detail.transactions.total / txPageSize))
    : 1;
  const campaignTotalPages = detail?.campaigns
    ? detail.campaigns.pages ?? detail.campaigns.total_pages ?? Math.max(1, Math.ceil(detail.campaigns.total / campaignPageSize))
    : 1;

  return (
    <>
    <ReuseableDrawer isOpen={!!orgId} onClose={onClose} title="Business Details" initialWidth="42%">
      {loading && !detail ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : detail ? (
        <div className="p-5 space-y-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{getOrgInitials(detail.org.name)}</span>
              </div>
              <div className="min-w-0">
                <p className="text-base font-bold text-gray-900 dark:text-gray-100 truncate">{detail.org.name}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${getOrgStatusStyle(detail.org.is_active)}`}>
                    {detail.org.is_active ? "active" : "inactive"}
                  </span>
                  {detail.org.type && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                      {detail.org.type}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsFundModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors shrink-0"
            >
              <Wallet size={14} /> Fund Wallet
            </button>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 dark:border-gray-800 px-3.5 py-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-8 h-8 rounded-lg bg-violet-50 dark:bg-violet-500/15 flex items-center justify-center shrink-0">
                <Sparkles size={15} className="text-violet-600 dark:text-violet-400" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">CXO AI insights</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {!canToggleAi
                    ? "Only a global admin can change this."
                    : aiEnabled === undefined
                    ? "Current setting unknown. Off by default."
                    : "AI summaries and \"ask your data\" on the CXO dashboard."}
                </p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={!!aiEnabled}
              aria-label="CXO AI insights"
              onClick={toggleCxoAi}
              disabled={aiSaving || !canToggleAi}
              title={canToggleAi ? undefined : "Only a global admin can change this"}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed ${aiEnabled ? "bg-violet-600" : "bg-gray-300 dark:bg-gray-700"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${aiEnabled ? "translate-x-5" : ""}`} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-[#7B7B7B] font-semibold dark:text-gray-500 uppercase tracking-wide mb-1">Domain</p>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100 break-words flex items-center gap-1.5">
                <Globe size={13} className="text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0" /> {detail.org.domain_name || "—"}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-[#7B7B7B] font-semibold dark:text-gray-500 uppercase tracking-wide mb-1">Created</p>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100 break-words flex items-center gap-1.5">
                <Calendar size={13} className="text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0" /> {formatOrgDate(detail.org.created_at)}
              </p>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Wallet</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3">
                <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">Balance</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(detail.wallet.balance, currency, "symbol")}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3">
                <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">Total Inflow</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(detail.wallet.total_inflow, currency, "symbol")}
                </p>
              </div>
            </div>
          </div>

          {detail.wallet.last_transaction && (
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Last Transaction</p>
              <div className="border border-gray-100 dark:border-gray-800 rounded-lg p-3 flex items-center gap-3">
                <span
                  className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    TRANSACTION_TYPE_STYLES[detail.wallet.last_transaction.transaction_type] ||
                    "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                  }`}
                >
                  <ArrowDownToLine size={14} />
                </span>
              
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                    {detail.wallet.last_transaction.comment || detail.wallet.last_transaction.source || "Transaction"}
                  </p>
                  <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">{formatOrgDate(detail.wallet.last_transaction.created_at)}</p>
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 shrink-0">
                  {formatCurrency(Number(detail.wallet.last_transaction.amount), detail.wallet.last_transaction.currency, "symbol")}
                </p>
              </div>
            </div>
          )}

          <div>
            <div className="flex border-b border-gray-100 dark:border-gray-800 mb-3">
              {(["transactions", "campaigns", "api-keys"] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide border-b-2 -mb-px transition-colors ${
                    activeTab === tab
                      ? "border-blue-600 text-blue-600 dark:text-blue-400"
                      : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  }`}
                >
                  {tab === "transactions" ? "Transactions" : tab === "campaigns" ? "Campaigns" : "API Keys"}
                  {tab === "transactions" && detail.transactions.total > 0 && ` (${detail.transactions.total})`}
                  {tab === "campaigns" && (detail.campaigns?.total ?? 0) > 0 && ` (${detail.campaigns!.total})`}
                </button>
              ))}
            </div>

            {activeTab === "api-keys" ? (
              <ApiKeysTab orgId={detail.org.id} />
            ) : activeTab === "transactions" ? (
              detail.transactions.data.length === 0 ? (
                <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">No transactions yet.</p>
              ) : (
                <div className="border border-gray-100 dark:border-gray-800 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[480px] text-left border-collapse">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-gray-800">
                          {["Type", "Amount", "Comment", "Date"].map((h) => (
                            <th key={h} className="px-3 py-2 text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detail.transactions.data.map((tx: OrgTransaction) => (
                          <tr key={tx.id} className="border-b border-gray-50 dark:border-gray-800/60 last:border-b-0">
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              <span
                                className={`text-[11px] font-medium px-2 py-0.5 rounded-full capitalize ${
                                  TRANSACTION_TYPE_STYLES[tx.transaction_type] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                                }`}
                              >
                                {tx.transaction_type}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100 whitespace-nowrap">
                              {formatCurrency(Number(tx.amount), tx.currency, "symbol")}
                            </td>
                            <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-300 truncate max-w-[160px]">{tx.comment || "—"}</td>
                            <td className="px-3 py-2.5 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 whitespace-nowrap">{formatOrgDate(tx.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {txTotalPages > 1 && (
                    <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-800">
                      <Pagination
                        currentPage={txPage}
                        totalPages={txTotalPages}
                        pageSize={txPageSize}
                        isLoading={loading}
                        onPageChange={setTxPage}
                      />
                    </div>
                  )}
                </div>
              )
            ) : !detail.campaigns || detail.campaigns.data.length === 0 ? (
              <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">No campaigns yet.</p>
            ) : (
              <div className="border border-gray-100 dark:border-gray-800 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-left border-collapse">
                    <thead>
                      <tr className="border-b border-gray-100 dark:border-gray-800">
                        {["Campaign", "Channel", "Status", "Size", "Cost", "Delivered", "Created"].map((h) => (
                          <th key={h} className="px-3 py-2 text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detail.campaigns.data.map((c: OrgCampaign) => (
                        <tr key={c.campaign_id} className="border-b border-gray-50 dark:border-gray-800/60 last:border-b-0">
                          <td className="px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100 truncate max-w-[160px]">{c.campaign_name || "—"}</td>
                          <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 capitalize whitespace-nowrap">{c.channel?.toLowerCase() || "—"}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span
                              className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                                CAMPAIGN_STATUS_STYLES[c.status] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                              }`}
                            >
                              {c.status?.charAt(0) + c.status?.slice(1).toLowerCase()}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{c.campaign_size?.toLocaleString() ?? "—"}</td>
                          <td className="px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100 whitespace-nowrap">
                            {formatCurrency(c.estimated_cost, currency, "symbol")}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {c.delivered_rate != null ? `${c.delivered_rate}%` : "—"}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 whitespace-nowrap">{formatOrgDate(c.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {campaignTotalPages > 1 && (
                  <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-800">
                    <Pagination
                      currentPage={campaignPage}
                      totalPages={campaignTotalPages}
                      pageSize={campaignPageSize}
                      isLoading={loading}
                      onPageChange={setCampaignPage}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="p-10 text-center text-sm text-gray-500 dark:text-gray-400">Couldn't load this business.</div>
      )}
    </ReuseableDrawer>

    {detail && (
      <FundWalletModal
        isOpen={isFundModalOpen}
        onClose={() => setIsFundModalOpen(false)}
        orgId={detail.org.id}
        orgName={detail.org.name}
        currentBalance={detail.wallet.balance}
        currency={currency}
        onFunded={fetchDetail}
      />
    )}
    </>
  );
};

// Global Admin's platform-wide Businesses view — GET /admin/organizations.
// Deliberately its own page/API, separate from the CRM's per-organization
// businesses table (crm-pages/businesses).
const GlobalAdminBusinesses: React.FC = () => {
  const token = localStorage.getItem("authToken");
  const [data, setData] = useState<OrganizationsListResponse | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const { makeRequest: getOrganizations } = useRequest("/admin/organizations", "GET", {
    Authorization: `Bearer ${token}`,
  });
  // Shared PATCH hook for both actions — the specific org + action is
  // supplied per-call via makeRequest's pathParam (e.g. "{id}/deactivate"),
  // matching how useRequest builds `${endpoint}/${pathParam}`.
  const { makeRequest: patchOrgStatus } = useRequest("/admin/organizations", "PATCH", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (page === 1) setInitialLoading(true);
      else setIsPaginationLoading(true);
      const [response] = (await getOrganizations(undefined, { page, limit: pageSize })) as [
        OrganizationsListResponse,
        number
      ];
      if (response?.status) setData(response);
      setInitialLoading(false);
      setIsPaginationLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  // PATCH /admin/organizations/{id}/deactivate or /reactivate — updates the
  // row (and the Active Businesses count) in place rather than refetching
  // the whole page.
  const handleToggleStatus = async (org: OrgSummary) => {
    const isActive = (org.status || "").toLowerCase() === "active";
    const action = isActive ? "deactivate" : "reactivate";
    setTogglingId(org.id);
    const [response, status] = await patchOrgStatus(undefined, undefined, `${org.id}/${action}`);
    setTogglingId(null);
    if (status >= 200 && status < 300 && response?.status !== false) {
      const nextStatus = isActive ? "inactive" : "active";
      toast.success(response?.message || `${org.name} ${action}d.`);
      setData((prev) =>
        prev
          ? {
              ...prev,
              organizations: prev.organizations.map((o) => (o.id === org.id ? { ...o, status: nextStatus } : o)),
              active_org_count: Math.max(0, prev.active_org_count + (isActive ? -1 : 1)),
            }
          : prev
      );
    } else {
      toast.error(response?.detail || response?.message || `Failed to ${action} ${org.name}.`);
    }
  };

  const organizations = data?.organizations || [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Businesses</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Every business on the platform, its wallet balance and activity.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <MetricCard
            icon={<Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
            iconClassName="bg-blue-50 dark:bg-blue-500/15"
            label="Total Businesses"
            value={(data?.total_org_count ?? 0).toLocaleString()}
          />
          <MetricCard
            icon={<CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />}
            iconClassName="bg-emerald-50 dark:bg-emerald-500/15"
            label="Active Businesses"
            value={(data?.active_org_count ?? 0).toLocaleString()}
          />
          <MetricCard
            icon={<Wallet className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
            iconClassName="bg-blue-50 dark:bg-blue-500/15"
            label="Total Wallet Volume"
            value={formatCurrency(data?.total_wallet_balance ?? 0, "NGN", "symbol")}
          />
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Business</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Type</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Wallet Balance</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Created</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Active</th>
                </tr>
              </thead>
              <tbody>
                {initialLoading || isPaginationLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Loading…</span>
                      </div>
                    </td>
                  </tr>
                ) : organizations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">
                      No businesses found yet.
                    </td>
                  </tr>
                ) : (
                  organizations.map((org) => (
                    <tr
                      key={org.id}
                      onClick={() => setSelectedOrgId(org.id)}
                      className="border-b border-gray-50 dark:border-gray-800 hover:bg-blue-50/30 dark:hover:bg-blue-500/10 transition-colors cursor-pointer group"
                    >
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center shrink-0">
                            <span className="text-xs font-bold text-blue-600 dark:text-blue-400">{getOrgInitials(org.name)}</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{org.name}</p>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-300 capitalize whitespace-nowrap">{org.type || "—"}</td>
                      <td className="py-3 px-2 text-sm text-gray-800 dark:text-gray-100 whitespace-nowrap">
                        {formatCurrency(org.wallet_balance, "NGN", "symbol")}
                      </td>
                      <td className="py-3 px-2">
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getOrgStatusStyle(org.status)}`}>
                          {org.status || "unknown"}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{formatOrgDate(org.created_at)}</td>
                      <td className="py-3 px-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(org)}
                          disabled={togglingId === org.id}
                          title={(org.status || "").toLowerCase() === "active" ? "Deactivate business" : "Reactivate business"}
                          className={`relative inline-flex h-5 w-9 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            (org.status || "").toLowerCase() === "active" ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-700"
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              (org.status || "").toLowerCase() === "active" ? "translate-x-4" : "translate-x-0"
                            }`}
                          />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pt-2">
            <Pagination
              currentPage={page}
              totalPages={data?.pages ?? 1}
              pageSize={pageSize}
              isLoading={isPaginationLoading}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <OrgDetailsDrawer orgId={selectedOrgId} onClose={() => setSelectedOrgId(null)} />
    </div>
  );
};

export default GlobalAdminBusinesses;
